/*
 * Pilotes USB-série — écran de dépannage.
 *
 * Sans le bon pilote, la carte n'apparaît pas du tout dans la fenêtre de choix
 * du port : ni dans BZØØK Live, ni ici. Le symptôme est trompeur, on croit à un
 * câble ou à une carte morte. Cet écran rassemble les trois pilotes du parc.
 *
 * Deux sources, dans cet ordre :
 *  1. le fichier déposé dans public/pilotes/, publié avec l'application — le
 *     seul moyen d'installer un pilote sur un site sans connexion ;
 *  2. à défaut, la page officielle du fabricant.
 *
 * Rien n'est embarqué par défaut : ce sont des binaires tiers, dont la
 * redistribution dépend de leur licence. Voir public/pilotes/LISEZMOI.txt.
 */

import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonText,
} from "@ionic/react";

type Pilote = {
  puce: string;
  identifiant: string;
  /** Ce que la fenêtre de choix du port affiche pour cette puce. */
  affiche: string;
  /** Nom attendu dans public/pilotes/ pour la copie hors ligne. */
  fichier: string;
  page: string;
};

const PILOTES: Pilote[] = [
  {
    puce: "CH340 / CH341",
    identifiant: "1A86:7523",
    affiche: "« USB Serial » ou « USB-SERIAL CH340 »",
    fichier: "CH341SER.EXE",
    page: "https://www.wch-ic.com/downloads/CH341SER_EXE.html",
  },
  {
    puce: "CH9102 / CH343",
    identifiant: "1A86:55D4",
    affiche: "« USB-Enhanced-SERIAL CH9102 »",
    fichier: "CH343SER.EXE",
    page: "https://www.wch-ic.com/downloads/CH343SER_EXE.html",
  },
  {
    puce: "CP210x",
    identifiant: "10C4:EA60",
    affiche: "« CP2102 USB to UART Bridge Controller »",
    fichier: "CP210x_Windows_Drivers.zip",
    page: "https://www.silabs.com/software-and-tools/usb-to-uart-bridge-vcp-drivers?tab=overview",
  },
  {
    puce: "FTDI FT232",
    identifiant: "0403:6001",
    affiche: "« USB Serial Port » ou « FT232R USB UART »",
    fichier: "FTDI_CDM_Windows.zip",
    page: "https://ftdichip.com/drivers/vcp-drivers/",
  },
];

/** "1A86:7523" à partir des deux nombres rendus par WebUSB. */
const enHexa = (vid: number, pid: number) =>
  `${vid.toString(16).padStart(4, "0")}:${pid.toString(16).padStart(4, "0")}`.toUpperCase();

const PilotesUsb: React.FC<{ onFermer: () => void }> = ({ onFermer }) => {
  const [locaux, setLocaux] = useState<Record<string, boolean>>({});
  const [identifie, setIdentifie] = useState<string | null>(null);
  const [messageIdent, setMessageIdent] = useState<string>("");

  /**
   * Identifie la puce sans pilote installé.
   *
   * C'est tout l'intérêt : tant que le pilote manque, la carte n'apparaît pas
   * dans la fenêtre de choix du PORT — on ne peut donc pas y lire son nom, et
   * c'est justement à ce moment qu'on a besoin de savoir quelle puce c'est.
   * L'USB, lui, énumère l'appareil quel que soit son pilote série : son
   * identifiant reste lisible.
   */
  const identifier = async () => {
    setIdentifie(null);
    const usb = (navigator as any).usb;
    if (!usb) {
      setMessageIdent("Ce navigateur n'expose pas l'USB. Voir la méthode manuelle ci-dessous.");
      return;
    }
    try {
      // filters vide : on veut voir tout ce qui est branché, y compris une
      // carte que Windows n'a pas su reconnaître.
      const appareil = await usb.requestDevice({ filters: [] });
      const id = enHexa(appareil.vendorId, appareil.productId);
      const connue = PILOTES.find((p) => p.identifiant === id);
      setIdentifie(id);
      setMessageIdent(
        connue
          ? `${id} — ${connue.puce}. Le pilote à installer est signalé ci-dessous.`
          : `${id} — puce absente de cette liste. Relever cet identifiant et le transmettre à BZØØK.`
      );
    } catch {
      setMessageIdent(
        "Aucune carte choisie. Si elle n'apparaît pas non plus dans cette fenêtre, voir la méthode manuelle ci-dessous."
      );
    }
  };

  useEffect(() => {
    let vivant = true;
    Promise.all(
      PILOTES.map(async (p) => {
        const url = `${process.env.PUBLIC_URL}/pilotes/${p.fichier}`;
        try {
          const r = await fetch(url, { method: "HEAD" });
          // Un hébergement de single page renvoie index.html en 200 pour tout
          // ce qu'il ne connaît pas : sans ce test, un fichier absent passerait
          // pour présent et le technicien téléchargerait une page HTML.
          const type = r.headers.get("content-type") ?? "";
          return [p.fichier, r.ok && !type.includes("text/html")] as const;
        } catch {
          return [p.fichier, false] as const;
        }
      })
    ).then((r) => {
      if (vivant) { setLocaux(Object.fromEntries(r)); }
    });
    return () => { vivant = false; };
  }, []);

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Pilotes USB-série</IonCardTitle>
        <IonCardSubtitle>
          À installer sur l'ordinateur quand une carte branchée en USB
          n'apparaît pas dans la fenêtre de choix du port.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <IonText>
          <p>
            Installer le pilote, <b>débrancher puis rebrancher</b> la carte, et
            rouvrir la fenêtre de choix du port.
          </p>
        </IonText>

        <IonButton onClick={identifier}>Identifier la carte branchée</IonButton>
        {messageIdent !== "" && (
          <IonText color={identifie ? "success" : "warning"}>
            <p>{messageIdent}</p>
          </IonText>
        )}

        <IonText>
          <p>
            <b>Si la carte n'apparaît nulle part</b> — ni dans la fenêtre de
            choix du port, ni dans celle ci-dessus — son identifiant reste
            lisible sous Windows : <i>Gestionnaire de périphériques</i> (Windows
            + X), repérer l'appareil marqué d'un triangle jaune, souvent nommé
            « USB2.0-Serial » ou « Périphérique inconnu », puis
            <i> Propriétés → Détails → Identifiants de matériel</i> : la ligne
            se lit <code>USB\VID_1A86&amp;PID_7523</code>, soit 1A86:7523 dans le
            tableau ci-dessous.
          </p>
          <p>
            Au pire, installer les quatre pilotes à la suite : ils sont signés,
            cohabitent sans problème, et seul celui de la puce présente sera
            utilisé.
          </p>
        </IonText>

        {PILOTES.map((p) => {
          const local = locaux[p.fichier] === true;
          const vise = identifie === p.identifiant;
          return (
            <div key={p.fichier} className="ota-pilote">
              <IonText color={vise ? "success" : undefined}>
                <p>
                  {vise ? "➡️ " : ""}
                  <b>{p.puce}</b> — {p.identifiant}
                  <br />
                  Affiché une fois le pilote installé : {p.affiche}
                </p>
              </IonText>
              {local ? (
                <IonButton
                  href={`${process.env.PUBLIC_URL}/pilotes/${p.fichier}`}
                  download={p.fichier}
                >
                  Télécharger {p.fichier}
                </IonButton>
              ) : (
                <IonButton
                  fill="outline"
                  href={p.page}
                  target="_blank"
                  rel="noreferrer"
                >
                  Site du fabricant (connexion nécessaire)
                </IonButton>
              )}
            </div>
          );
        })}

        <IonText color="medium">
          <p>
            Les fichiers proposés ici sont pour <b>Windows</b>. Pour macOS ou
            Linux, passer par la page du fabricant.
          </p>
        </IonText>

        <IonButton color="medium" onClick={onFermer}>
          Retour
        </IonButton>
      </IonCardContent>
    </IonCard>
  );
};

export default PilotesUsb;
